import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { PERM_GROUPS, PERM_MODULES, LEVELS, LEVEL_LABEL, defaultLevel, PRESETS, ROLE_PRESET, ROLES, roleLabel } from '../lib/permModules'

async function api(method, body, query = '') {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch('/api/admin' + query, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (session?.access_token || '') },
    body: body ? JSON.stringify(body) : undefined,
  })
  const j = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(j.error || ('HTTP ' + res.status))
  return j
}
function genPassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  let s = ''
  const a = new Uint32Array(12); (window.crypto || window.msCrypto).getRandomValues(a)
  a.forEach(n => { s += chars[n % chars.length] })
  return s
}
const fmtDate = d => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '\u2014')
const LV_COL = { none: '#8a97a6', read: '#3b7bff', write: '#137050', full: '#b5843f' }

const CSS = `
.cl-card{background:var(--card);border:1px solid var(--line);border-radius:14px;padding:20px}
.cl-tbl{width:100%;border-collapse:collapse}
.cl-tbl th{text-align:left;font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;padding:0 10px 10px}
.cl-tbl td{padding:12px 10px;border-top:1px solid var(--line);font-size:13px;color:var(--text);vertical-align:middle}
.cl-pill{display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:600;padding:3px 9px;border-radius:11px;white-space:nowrap}
.cl-act{background:transparent;border:1px solid var(--line);color:var(--text);border-radius:7px;padding:5px 10px;font-size:12px;cursor:pointer;font-family:inherit;white-space:nowrap}
.cl-act:hover{border-color:var(--gold2)}
.cl-act.red{color:#c0434a}
.cl-bg{position:fixed;inset:0;background:rgba(10,22,40,.55);z-index:3000;display:flex;align-items:flex-start;justify-content:center;padding:40px 16px;overflow-y:auto}
.cl-modal{background:var(--card);border-radius:14px;padding:24px;width:100%;box-shadow:0 20px 60px rgba(10,22,40,.35)}
.cl-lab{display:block;font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.05em;margin-bottom:5px}
.cl-in{width:100%;box-sizing:border-box;padding:9px 11px;border-radius:8px;border:1px solid var(--line);background:var(--bg2,var(--bg));color:var(--text);font-size:13px;font-family:inherit}
.cl-seg{display:inline-flex;border:1px solid var(--line);border-radius:8px;overflow:hidden}
.cl-seg button{border:none;background:transparent;color:var(--muted);font-size:11.5px;font-weight:600;padding:5px 10px;cursor:pointer;font-family:inherit;border-right:1px solid var(--line)}
.cl-seg button:last-child{border-right:none}
.cl-preset{border:1px solid var(--line);background:transparent;color:var(--text);border-radius:16px;padding:5px 12px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit}
.cl-preset:hover{border-color:var(--gold2);color:var(--gold2)}
.cl-grp{font-size:11px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:var(--muted);padding:14px 0 6px}
.cl-prow{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:6px 0;border-top:1px solid var(--line)}
@media (max-width:1023px){.cl-hide-sm{display:none}}
`

function PermEditor({ value, onChange }) {
  const lv = k => value[k] || defaultLevel(k)
  const setAll = l => onChange(Object.fromEntries(PERM_MODULES.map(([k]) => [k, l])))
  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 4 }}>Presets:</span>
        {Object.entries(PRESETS).map(([k, p]) => <button key={k} type="button" className="cl-preset" onClick={() => onChange(p.perms())}>{p.label}</button>)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', marginRight: 4 }}>Set all:</span>
        {LEVELS.map(l => <button key={l} type="button" className="cl-preset" onClick={() => setAll(l)}>{LEVEL_LABEL[l]}</button>)}
      </div>
      {PERM_GROUPS.map(([g, mods]) => (
        <div key={g}>
          <div className="cl-grp">{g}</div>
          {mods.map(([k, label]) => (
            <div key={k} className="cl-prow">
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{label}</span>
              <div className="cl-seg">
                {LEVELS.map(l => {
                  const on = lv(k) === l
                  return <button key={l} type="button" onClick={() => onChange({ ...value, [k]: l })}
                    style={on ? { background: LV_COL[l], color: '#fff' } : undefined}>{LEVEL_LABEL[l]}</button>
                })}
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  )
}

function summary(perms) {
  const p = perms || {}
  const c = { none: 0, read: 0, write: 0, full: 0 }
  PERM_MODULES.forEach(([k]) => { c[p[k] || defaultLevel(k)]++ })
  return c
}

export default function CrewLogins({ vessels: vesselsProp }) {
  const { config } = useAuth()
  const vessels = vesselsProp && vesselsProp.length ? vesselsProp : (config?.id ? [{ id: config.id, vessel_name: config.vessel_name }] : [])
  const vkey = vessels.map(v => v.id).join(',')
  const multi = vessels.length > 1
  const vname = id => (vessels.find(v => String(v.id) === String(id)) || {}).vessel_name || ''
  const [crew, setCrew] = useState([])
  // the vessels' crew lists (names): each login is linked to its own crew record - its own personal data and documents
  const [members, setMembers] = useState({})
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [add, setAdd] = useState(null)        // form for new login
  const [perm, setPerm] = useState(null)      // { crew, form }
  const [pw, setPw] = useState(null)          // { crew, password }
  const [done, setDone] = useState(null)      // credentials to hand over
  const [busy, setBusy] = useState(false)

  async function load() {
    if (!vessels.length) { setCrew([]); setLoading(false); return }
    setLoading(true); setErr('')
    try {
      const all = await Promise.all(vessels.map(v => api('GET', null, '?resource=my_crew&client_id=' + encodeURIComponent(v.id)).then(j => ({ v: v.id, crew: j.crew || [], members: j.crew_members || [] }))))
      setCrew(all.flatMap(x => x.crew))
      setMembers(Object.fromEntries(all.map(x => [x.v, x.members])))
    }
    catch (e) { setErr(e.message) }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [vkey]) // eslint-disable-line

  async function run(fn) {
    setBusy(true); setErr('')
    try { await fn(); await load() } catch (e) { setErr(e.message); return false } finally { setBusy(false) }
    return true
  }

  async function createLogin() {
    const f = add
    if (!f.name || !f.email || !f.password) { setErr('Name, email and password are required'); return }
    if (f.password.length < 8) { setErr('Password must be at least 8 characters'); return }
    const ok = await run(() => api('POST', { action: 'my_crew_create', client_id: f.client_id || vessels[0]?.id, name: f.name.trim(), email: f.email.trim().toLowerCase(), password: f.password, role: f.role, permissions: f.permissions, ...(f.crew_member_id ? { crew_member_id: f.crew_member_id } : {}) }))
    if (ok) { setDone({ name: f.name, email: f.email.trim().toLowerCase(), password: f.password }); setAdd(null) }
  }
  async function savePerms() {
    const ok = await run(() => api('POST', { action: 'my_crew_update', crew_id: perm.crew.id, permissions: perm.form, role: perm.role, ...(perm.link !== (perm.crew.crew_id || '') ? { crew_member_id: perm.link || null } : {}) }))
    if (ok) setPerm(null)
  }
  async function savePassword() {
    if (!pw.password || pw.password.length < 8) { setErr('Password must be at least 8 characters'); return }
    const ok = await run(() => api('POST', { action: 'my_crew_password', crew_id: pw.crew.id, password: pw.password }))
    if (ok) { setDone({ name: pw.crew.name, email: pw.crew.email, password: pw.password }); setPw(null) }
  }
  const toggleActive = c => {
    if (c.active && !window.confirm('Deactivate ' + c.name + '? They will not be able to sign in until reactivated.')) return
    run(() => api('POST', { action: 'my_crew_update', crew_id: c.id, active: !c.active }))
  }
  const remove = c => {
    if (!window.confirm('Permanently delete the login for ' + c.name + '? Their records stay in the system; only the login is removed.')) return
    run(() => api('POST', { action: 'my_crew_delete', crew_id: c.id }))
  }
  const openAdd = () => { const role = 'crew'; setErr(''); setAdd({ client_id: vessels[0]?.id, name: '', email: '', role, password: genPassword(), permissions: PRESETS[ROLE_PRESET[role]].perms() }) }

  const copy = t => { try { navigator.clipboard.writeText(t) } catch (e) {} }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '4px 4px 40px' }}>
      <style>{CSS}</style>
      <div className="cl-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>Crew Logins &middot; {multi ? vessels.length + ' vessels' : vessels[0]?.vessel_name}</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 4, maxWidth: 640, lineHeight: 1.5 }}>
              Each crew member signs in with their own email. You decide what they can see and edit, module by module. Changes apply the next time they sign in or refresh.
            </div>
          </div>
          <button className="btn btn-primary" onClick={openAdd}>+ Add crew login</button>
        </div>

        {err && !add && !perm && !pw && <div style={{ background: 'rgba(192,67,74,.1)', color: '#c0434a', fontSize: 12.5, padding: '9px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}

        {loading ? <div style={{ color: 'var(--muted)', fontSize: 13, padding: 20 }}>Loading&hellip;</div>
          : crew.length === 0 ? <div style={{ color: 'var(--muted)', fontSize: 13, padding: '28px 10px', textAlign: 'center' }}>No crew logins yet. Add one to give a crew member their own access.</div>
          : (
            <div style={{ overflowX: 'auto' }}>
              <table className="cl-tbl">
                <thead><tr>{multi && <th>Vessel</th>}<th>Name</th><th className="cl-hide-sm">Email</th><th>Role</th><th>Access</th><th className="cl-hide-sm">Last login</th><th>Status</th><th /></tr></thead>
                <tbody>
                  {crew.map(c => {
                    const s = summary(c.permissions)
                    return (
                      <tr key={c.id} style={{ opacity: c.active ? 1 : 0.55 }}>
                        {multi && <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{vname(c.client_id)}</td>}
                        <td style={{ fontWeight: 600 }}>{c.name}{c.crew_id === null && <span className="cl-pill" title="Not linked to a crew record: this login sees no personal data or documents of its own. Link it under Permissions." style={{ marginLeft: 6, fontWeight: 500, background: 'rgba(214,150,40,.14)', color: '#8a5c0a' }}>not linked</span>}</td>
                        <td className="cl-hide-sm" style={{ color: 'var(--muted)' }}>{c.email}</td>
                        <td>{roleLabel(c.role)}</td>
                        <td style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                          <span style={{ color: LV_COL.full }}>{s.full + s.write}</span> edit &middot; <span style={{ color: LV_COL.read }}>{s.read}</span> view &middot; <span>{s.none}</span> hidden
                        </td>
                        <td className="cl-hide-sm" style={{ color: 'var(--muted)' }}>{fmtDate(c.last_login)}</td>
                        <td>{c.active
                          ? <span className="cl-pill" style={{ background: 'rgba(31,157,99,.12)', color: '#137050' }}>Active</span>
                          : <span className="cl-pill" style={{ background: 'rgba(138,151,166,.16)', color: '#5b6878' }}>Deactivated</span>}</td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <button className="cl-act" onClick={() => { setErr(''); setPerm({ crew: c, form: { ...(c.permissions || {}) }, role: c.role || 'crew', link: c.crew_id || '' }) }}>Permissions</button>
                            <button className="cl-act" onClick={() => { setErr(''); setPw({ crew: c, password: genPassword() }) }}>Reset password</button>
                            <button className="cl-act" disabled={busy} onClick={() => toggleActive(c)}>{c.active ? 'Deactivate' : 'Reactivate'}</button>
                            <button className="cl-act red" disabled={busy} onClick={() => remove(c)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {add && (
        <div className="cl-bg" onClick={() => setAdd(null)}>
          <div className="cl-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>Add crew login</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              {multi && (
                <label style={{ gridColumn: '1 / -1' }}><span className="cl-lab">Vessel</span>
                  <select className="cl-in" value={add.client_id} onChange={e => setAdd({ ...add, client_id: e.target.value })}>
                    {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
                  </select>
                </label>
              )}
              <label><span className="cl-lab">Full name</span><input className="cl-in" value={add.name} onChange={e => setAdd({ ...add, name: e.target.value })} /></label>
              <label><span className="cl-lab">Email</span><input className="cl-in" type="email" value={add.email} onChange={e => setAdd({ ...add, email: e.target.value })} /></label>
              <label><span className="cl-lab">Role</span>
                <select className="cl-in" value={add.role} onChange={e => { const role = e.target.value; setAdd({ ...add, role, permissions: PRESETS[ROLE_PRESET[role] || 'readonly'].perms() }) }}>
                  {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label style={{ gridColumn: '1 / -1' }}><span className="cl-lab">Crew record (their own personal data and documents)</span>
                <select className="cl-in" value={add.crew_member_id || ''} onChange={e => setAdd({ ...add, crew_member_id: e.target.value })}>
                  <option value="">Find by name (only if exactly one crew member has it)</option>
                  {(members[add.client_id || vessels[0]?.id] || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
              <label><span className="cl-lab">Temporary password</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input className="cl-in" value={add.password} onChange={e => setAdd({ ...add, password: e.target.value })} />
                  <button type="button" className="cl-act" onClick={() => setAdd({ ...add, password: genPassword() })}>New</button>
                </div>
              </label>
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Access is pre-set from the role. Adjust anything below.</div>
            <div style={{ maxHeight: '42vh', overflowY: 'auto', paddingRight: 6 }}><PermEditor value={add.permissions} onChange={p => setAdd({ ...add, permissions: p })} /></div>
            {err && <div style={{ color: '#c0434a', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={() => setAdd(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy} onClick={createLogin}>{busy ? 'Creating\u2026' : 'Create login'}</button>
            </div>
          </div>
        </div>
      )}

      {perm && (
        <div className="cl-bg" onClick={() => setPerm(null)}>
          <div className="cl-modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Permissions &middot; {perm.crew.name}{multi ? ' \u00b7 ' + vname(perm.crew.client_id) : ''}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>Hidden removes the module from their menu. View is read-only. Edit can add and change. Full can also delete.</div>
              </div>
              <select className="cl-in" style={{ width: 170 }} value={perm.role} onChange={e => setPerm({ ...perm, role: e.target.value })}>
                {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {perm.crew.crew_id !== undefined && (
              <label style={{ display: 'block', marginBottom: 10 }}><span className="cl-lab">Crew record (their own personal data and documents)</span>
                <select className="cl-in" value={perm.link} onChange={e => setPerm({ ...perm, link: e.target.value })}>
                  <option value="">Not linked</option>
                  {(members[perm.crew.client_id] || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </label>
            )}
            <div style={{ maxHeight: '55vh', overflowY: 'auto', paddingRight: 6 }}><PermEditor value={perm.form} onChange={p => setPerm({ ...perm, form: p })} /></div>
            {err && <div style={{ color: '#c0434a', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={() => setPerm(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy} onClick={savePerms}>{busy ? 'Saving\u2026' : 'Save permissions'}</button>
            </div>
          </div>
        </div>
      )}

      {pw && (
        <div className="cl-bg" onClick={() => setPw(null)}>
          <div className="cl-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>Reset password &middot; {pw.crew.name}</div>
            <span className="cl-lab">New password</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="cl-in" value={pw.password} onChange={e => setPw({ ...pw, password: e.target.value })} />
              <button type="button" className="cl-act" onClick={() => setPw({ ...pw, password: genPassword() })}>New</button>
            </div>
            {err && <div style={{ color: '#c0434a', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={() => setPw(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy} onClick={savePassword}>{busy ? 'Saving\u2026' : 'Set password'}</button>
            </div>
          </div>
        </div>
      )}

      {done && (
        <div className="cl-bg" onClick={() => setDone(null)}>
          <div className="cl-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Login ready for {done.name}</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14 }}>Hand these over privately. This password is not shown again.</div>
            <div style={{ background: 'var(--bg2, var(--bg))', border: '1px solid var(--line)', borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.9, color: 'var(--text)' }}>
              <div><span style={{ color: 'var(--muted)' }}>App: </span>{window.location.origin}</div>
              <div><span style={{ color: 'var(--muted)' }}>Email: </span>{done.email}</div>
              <div><span style={{ color: 'var(--muted)' }}>Password: </span><b style={{ fontFamily: 'monospace' }}>{done.password}</b></div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn" onClick={() => copy('Hey Captain login\n' + window.location.origin + '\nEmail: ' + done.email + '\nPassword: ' + done.password)}>Copy</button>
              <button className="btn btn-primary" onClick={() => setDone(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
